import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { toLocal } from "@/components/activities/ActivityDialog";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const iso = (local) => (local ? new Date(local).toISOString() : null);

const Shell = ({ open, onOpenChange, title, desc, children, onSave, busy, disabled, testId, saveLabel = "Salva", danger }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-md" data-testid={testId}>
      <DialogHeader><DialogTitle>{title}</DialogTitle>{desc && <DialogDescription>{desc}</DialogDescription>}</DialogHeader>
      <div className="space-y-3">{children}</div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
        <Button onClick={onSave} disabled={busy || disabled} className={danger ? "bg-red-600 hover:bg-red-700" : ""} data-testid={`${testId}-save`}>{saveLabel}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const useRun = (onOpenChange, onDone) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.data?.outlook_error) toast.warning(`Salvato nel CRM. Outlook: ${r.data.outlook_error}`); else toast.success(ok);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return [busy, run];
};

export const PlanDialog = ({ open, onOpenChange, itv, onDone }) => {
  const [f, setF] = useState({});
  const [busy, run] = useRun(onOpenChange, onDone);
  useEffect(() => { if (open) setF({ start: toLocal(itv.scheduled_at), end: toLocal(itv.scheduled_end_at), outlook: itv.activity_id ? undefined : true }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const valid = f.start && f.end && new Date(f.end) > new Date(f.start);
  return (
    <Shell open={open} onOpenChange={onOpenChange} title={itv.scheduled_at ? "Ripianifica intervento" : "Pianifica intervento"} testId="itv-plan-dialog" busy={busy} disabled={!valid}
      desc="La visita viene registrata come appuntamento in Agenda. Con Outlook, l'evento è nel tuo calendario e i tecnici ricevono l'invito."
      onSave={() => run(() => api.post(`/interventions/${itv.id}/plan`, { start_at: iso(f.start), end_at: iso(f.end), outlook: !!f.outlook }), "Intervento pianificato")}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label className="text-xs">Inizio</Label><Input type="datetime-local" value={f.start || ""} onChange={(e) => setF((s) => ({ ...s, start: e.target.value, end: s.end || "" }))} data-testid="itv-plan-start" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Fine</Label><Input type="datetime-local" value={f.end || ""} onChange={(e) => setF((s) => ({ ...s, end: e.target.value }))} data-testid="itv-plan-end" /></div>
      </div>
      {f.outlook !== undefined && <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!f.outlook} onCheckedChange={(v) => setF((s) => ({ ...s, outlook: !!v }))} data-testid="itv-plan-outlook" />Aggiungi a Outlook e invita i tecnici</label>}
    </Shell>
  );
};

export const AssignDialog = ({ open, onOpenChange, itv, technicians, onDone }) => {
  const [lead, setLead] = useState("");
  const [support, setSupport] = useState([]);
  const [busy, run] = useRun(onOpenChange, onDone);
  useEffect(() => { if (open) { setLead(itv.lead_technician_id || ""); setSupport(itv.technician_ids || []); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id) => setSupport((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <Shell open={open} onOpenChange={onOpenChange} title="Assegna tecnici" testId="itv-assign-dialog" busy={busy} disabled={!lead}
      desc={itv.activity_id ? "Se l'intervento è su Outlook, gli invitati dell'evento vengono aggiornati." : undefined}
      onSave={() => run(() => api.post(`/interventions/${itv.id}/assign`, { lead_technician_id: lead, technician_ids: support.filter((x) => x !== lead) }), "Assegnazione salvata")}>
      <ul className="divide-y divide-line rounded-control border border-line">
        {technicians.map((t) => (
          <li key={t.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`itv-tech-${t.id}`}>
            <input type="radio" name="lead" checked={lead === t.id} onChange={() => setLead(t.id)} aria-label={`Responsabile ${t.name}`} data-testid={`itv-tech-lead-${t.id}`} />
            <div className="min-w-0 flex-1"><p className="text-sm font-medium text-ink">{t.name}</p><p className="truncate text-xs text-slate-500">{[t.area, ...(t.skills || [])].filter(Boolean).join(" · ")}</p></div>
            {lead !== t.id && <label className="flex items-center gap-1.5 text-xs text-slate-600"><Checkbox checked={support.includes(t.id)} onCheckedChange={() => toggle(t.id)} data-testid={`itv-tech-support-${t.id}`} />Supporto</label>}
            {lead === t.id && <span className="text-xs font-semibold text-brand-700">Responsabile</span>}
          </li>
        ))}
        {!technicians.length && <li className="px-3 py-4 text-sm text-slate-500">Nessun tecnico disponibile.</li>}
      </ul>
    </Shell>
  );
};

export const ReasonDialog = ({ open, onOpenChange, itv, to, title, desc, saveLabel, danger, onDone, optional }) => {
  const [reason, setReason] = useState("");
  const [busy, run] = useRun(onOpenChange, onDone);
  useEffect(() => { if (open) setReason(""); }, [open]);
  return (
    <Shell open={open} onOpenChange={onOpenChange} title={title} desc={desc} testId="itv-reason-dialog" busy={busy} disabled={!optional && reason.trim().length < 3} saveLabel={saveLabel} danger={danger}
      onSave={() => run(() => api.post(`/interventions/${itv.id}/status`, { to, reason: reason.trim() || null }), "Stato aggiornato")}>
      <Label className="text-xs">{optional ? "Note (facoltative)" : "Motivo"}</Label>
      <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="itv-reason-input" />
    </Shell>
  );
};

export const SlaDialog = ({ open, onOpenChange, itv, onDone }) => {
  const [f, setF] = useState({});
  const [busy, run] = useRun(onOpenChange, onDone);
  useEffect(() => { if (open) setF({ resp: toLocal(itv.sla?.response_due_at), res: toLocal(itv.sla?.resolution_due_at), reason: "" }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Shell open={open} onOpenChange={onOpenChange} title="Modifica SLA dell'intervento" testId="itv-sla-dialog" busy={busy} disabled={f.reason?.trim().length < 3}
      desc="Le nuove scadenze sostituiscono quelle della regola SLA (override). La modifica viene registrata in Timeline e Audit."
      onSave={() => run(() => api.patch(`/interventions/${itv.id}/sla`, { response_due_at: iso(f.resp), resolution_due_at: iso(f.res), reason: f.reason }), "SLA aggiornato")}>
      <div className="space-y-1.5"><Label className="text-xs">Scadenza presa in carico</Label><Input type="datetime-local" value={f.resp || ""} onChange={(e) => setF((s) => ({ ...s, resp: e.target.value }))} data-testid="itv-sla-response" /></div>
      <div className="space-y-1.5"><Label className="text-xs">Scadenza risoluzione</Label><Input type="datetime-local" value={f.res || ""} onChange={(e) => setF((s) => ({ ...s, res: e.target.value }))} data-testid="itv-sla-resolution" /></div>
      <div className="space-y-1.5"><Label className="text-xs">Motivo</Label><Textarea rows={2} value={f.reason || ""} onChange={(e) => setF((s) => ({ ...s, reason: e.target.value }))} data-testid="itv-sla-reason" /></div>
    </Shell>
  );
};
